/**
 * useDeleteStationImage - Mutation hook for deleting station images
 * Supports optimistic updates with rollback on error
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteStationImage } from '../../api/stationImages';
import {
  StationImageType,
  IStationImagesResponse,
} from '../../components/StationMedia/types';
import { STATION_IMAGES_QUERY_KEY } from './useStationImages';

interface DeleteImageParams {
  imageId: string;
  imageType: StationImageType;
}

interface UseDeleteStationImageOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for deleting station images with optimistic updates
 *
 * @param stationId - The station/location UUID
 * @param options - Callback options
 * @returns Mutation object with delete function
 *
 * @example
 * const { mutate: deleteImage, isLoading } = useDeleteStationImage(stationId);
 * deleteImage({ imageId: 'xxx', imageType: 'gallery' });
 */
const useDeleteStationImage = (
  stationId: string,
  options?: UseDeleteStationImageOptions
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteImageParams, { previous?: IStationImagesResponse }>({
    mutationFn: async ({ imageId }) => {
      await deleteStationImage(stationId, imageId);
    },
    // Optimistic update
    onMutate: async ({ imageId, imageType }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: [STATION_IMAGES_QUERY_KEY, stationId],
        });

        // Snapshot the previous value
        const previous = queryClient.getQueryData<IStationImagesResponse>([
          STATION_IMAGES_QUERY_KEY,
          stationId,
        ]);

        // Optimistically remove the image
        queryClient.setQueryData<IStationImagesResponse>(
          [STATION_IMAGES_QUERY_KEY, stationId],
          (old) => {
            if (!old) return old;

            if (imageType === 'icon' && old.icon?.id === imageId) {
              return { ...old, icon: null };
            } else if (imageType === 'banner' && old.banner?.id === imageId) {
              return { ...old, banner: null };
            } else if (imageType === 'gallery') {
              return {
                ...old,
                gallery: old.gallery.filter((img) => img.id !== imageId),
              };
            }
            return old;
          }
        );

        return { previous };
      },

      // Rollback on error
      onError: (error, variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(
            [STATION_IMAGES_QUERY_KEY, stationId],
            context.previous
          );
        }
        options?.onError?.(error);
      },

      // Refetch after success or error
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [STATION_IMAGES_QUERY_KEY, stationId],
        });
      },

    onSuccess: () => {
      options?.onSuccess?.();
    },
  });
};

export default useDeleteStationImage;
